import {
	BehaviorSubject,
	NEVER,
	Observable,
	of,
	Subject,
} from 'rxjs';

import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { WebAuth } from 'auth0-js';
import { environment } from 'src/environments/environment';
import { ApiMethod } from '../decorators/api-method.decorator';
import { Router } from '@angular/router';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class AuthService {

	public static EmailClaim = 'email';
	public static PlatformTypeIdClaim = 'V-PlatformType-ID';
	public static UserAccountIdClaim = 'V-UserAccount-ID';
	public static AccountIdClaim = 'V-Account-ID';
	public static UserIdClaim = 'V-User-ID';

	public static VtrTokenKey = 'vtr_token';
	public static Auth0IdTokenKey = 'id_token';

	private auth0 = new WebAuth({
		clientID: environment.auth.clientId,
		domain: environment.auth.domain,
		redirectUri: `${window.location.origin}/callback`,
		audience: `https://${environment.auth.domain}/userinfo`,
		responseType: 'token id_token',
		scope: 'openid email'
	});

	constructor(
		public http: HttpClient,
		private jwtHelper: JwtHelperService,
		private router: Router
	) { }

	@ApiMethod()
	public generateVtrToken(platformTypeId?: number, accountId?: number, userAccountId: number = null): Observable<{ token: string }> {
		const authToken = this.getAuth0Token();

		const formData = new FormData();
		if (!!accountId) { formData.set('accountId', accountId.toString()); }
		if (!!platformTypeId) { formData.set('platformTypeId', platformTypeId.toString()); }
		let url = `${environment.baseUrl}/userauth/generate-token`;
		if(!!userAccountId){
			url = `${url}?userAccountId=${userAccountId}`;
		}
		return this.http
			.post<{ token: string }>(url, formData, {
				headers: new HttpHeaders({
					Authorization: 'Bearer ' + authToken
				})
			});
	}

	@ApiMethod()
	public isUserAuthenticated(email?: string): Observable<boolean> {
		return this.http
			.get<boolean>(`${environment.baseUrl}/userauth/is-authenticated`, {
				headers: this.getAuthenticationHeader(),
				params: {
					email: email || ''
				}
			});
	}

	@ApiMethod()
	public isVendorUserSmartSyncOnly(email: string): Observable<boolean> {
		return this.http.get<boolean>(`${environment.baseUrl}/userauth/is-vendor-user-smart-sync-only`, {
			params: {
				email: email || ''
			}
		});
	}

	private _clearSession() {
		localStorage.removeItem(AuthService.Auth0IdTokenKey);
		localStorage.removeItem(AuthService.VtrTokenKey);
	}

	public removeAuth0Token(): void {
		localStorage.removeItem(AuthService.Auth0IdTokenKey);
	}
	
	public login() {
		this._clearSession();
		this.auth0.authorize({
			prompt: 'login'
		});
	}

	public handleAuth0Token(): Observable<boolean> {
		const result = new Subject<boolean>();

		this.auth0.parseHash((err, authResult) => {
			if (authResult && authResult.accessToken && authResult.idToken) {
				localStorage.setItem(AuthService.Auth0IdTokenKey, authResult.idToken);
				result.next(true);
			} else {
				this._clearSession();
				result.next(false);
			}
		});

		return result;
	}

	public handleVtrTokenGeneration(platformTypeId?: number, accountId?: number, userAccountId: number = null): Observable<boolean> {
		return this.generateVtrToken(platformTypeId, accountId, userAccountId)
			.pipe(
				catchError(() => {
					this._clearSession();
					return of({ token: null });
				}),
				map(({ token }) => {
					if (!!token) {
						localStorage.setItem(AuthService.VtrTokenKey, token);
					}

					return !!token;
				})
			);
	}

	public regenerateToken(platformTypeId?: number, accountId?: number): Observable<boolean> {
		return this.isUserAuthenticated()
			.pipe(mergeMap(isAuthenticated => {
				if (!isAuthenticated) {
					this._clearSession();
					this.router.navigate(['/login']);
					return of(false);
				} else {
					return this.handleVtrTokenGeneration(platformTypeId, accountId);
				}
			}));
	}

	public logout() {
		this._clearSession();
		this.auth0.logout({
			returnTo: window.location.origin,
		});
	}

	public isLoggedIn(): boolean {
		const auth0Token = this.getAuth0Token();
		const vtrToken = this.getVtrToken();

		return !this.jwtHelper.isTokenExpired(auth0Token) && !this.jwtHelper.isTokenExpired(vtrToken);
	}

	public isLoggedInWithVtrUserAccount(): boolean {
		const auth0Token = this.getAuth0Token();
		const vtrToken = this.getVtrToken();

		return !!auth0Token && !!vtrToken &&
			!this.jwtHelper.isTokenExpired(auth0Token) && 
			!this.jwtHelper.isTokenExpired(vtrToken) && 
			!!(this.jwtHelper.decodeToken(vtrToken)["V-UserAccount-ID"]);
	}

	public getCurrentAccount(): number {
		const vtrToken = this.getVtrToken();
		return  !!vtrToken 
					&& !this.jwtHelper.isTokenExpired(vtrToken)  
				? +(this.jwtHelper.decodeToken(vtrToken)[AuthService.AccountIdClaim]) : -1;
	}

	public getUserAccountId(): number {
		const vtrToken = this.getVtrToken();
		return  !!vtrToken 
					&& !this.jwtHelper.isTokenExpired(vtrToken)  
				? +(this.jwtHelper.decodeToken(vtrToken)[AuthService.UserAccountIdClaim]) : -1;
	}

	public getAuthenticationHeader(): HttpHeaders {
		const authToken = this.getAuth0Token();
		const vtrToken = this.getVtrToken();

		const authorizationHeader = new HttpHeaders({
			Authorization: 'Bearer ' + authToken,
			'X-Vitralogy-Authorize': 'Bearer ' + vtrToken
		});

		return authorizationHeader;
	}

	public getSimpleAuthenticationHeader(): HttpHeaders {
		const authToken = this.getAuth0Token();
		
		const authorizationHeader = new HttpHeaders({
			Authorization: 'Bearer ' + authToken			
		});

		return authorizationHeader;
	}
	public getAuth0Token(): string {
		return localStorage.getItem(AuthService.Auth0IdTokenKey);
	}

	public getVtrToken(): string {
		return localStorage.getItem(AuthService.VtrTokenKey);
	}


	public getVtrTokenHeader(): HttpHeaders {
		const vtrToken = this.getVtrToken();

		const authorizationHeader = new HttpHeaders({
			Authorization: 'Bearer ' + vtrToken
		});

		return authorizationHeader;
	}
}
